
import React, { useState } from 'react'
import { Dialog } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface CreateFolderModalProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onCreate: (name: string) => Promise<void>
  isCreating?: boolean
}

export const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
  isOpen,
  onOpenChange,
  onCreate,
  isCreating = false,
}) => {
  const [folderName, setFolderName] = useState('')
  const [error, setError] = useState('')

  const handleCreate = async () => {
    // Client-side validation
    const trimmedName = folderName.trim()

    if (!trimmedName) {
      setError('Folder name cannot be empty')
      return
    }

    if (trimmedName.length > 50) {
      setError('Folder name must be less than 50 characters')
      return
    }

    // Call onCreate and let parent handle API call
    await onCreate(trimmedName)

    // Reset and close
    setFolderName('')
    setError('')
    onOpenChange(false)
  }

  const handleClose = () => {
    setFolderName('')
    setError('')
    onOpenChange(false)
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={handleClose}
      title="Create New Folder"
      description="Enter a name for your new folder."
      showCloseButton={false}
    >
      <div className="py-4">
        <div className="space-y-2">
          <Label htmlFor="folder-name">Folder Name</Label>
          <Input
            id="folder-name"
            type="text"
            placeholder="Enter folder name"
            value={folderName}
            onChange={(e) => {
              setFolderName(e.target.value)
              setError('') // Clear error on change
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isCreating) {
                handleCreate()
              }
            }}
            disabled={isCreating}
            autoFocus
            maxLength={50}
          />
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <Button
          variant="ghost"
          onClick={handleClose}
          disabled={isCreating}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          disabled={isCreating || !folderName.trim()}
        >
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
      </div>
    </Dialog>
  )
}